import Cookies from 'js-cookie';

import { initializeAnalytics, initializeIntercom } from '@bonusly/analytics';
import { useApplication } from '@bonusly/application';
import { HttpError } from '@bonusly/http';

import { CSRF_COOKIE_ID } from '../../config/csrf';
import { Company, useIdentityStore, User } from '../../lib';
import { cleanPath, joinPath } from '../../utils/url';
import { initializeHotJar } from '../hotjar';

const createFetcher =
	<T>(path: string) =>
	async (): Promise<T> => {
		const csrfToken = Cookies.get(CSRF_COOKIE_ID);

		const { token } = useIdentityStore.getState();
		const profileBaseURL = joinPath(useApplication.getState().application.recognizeOrigin, '/api/v2/profile');

		const url = new URL(path, cleanPath(profileBaseURL || ''));
		url.searchParams.append('view', 'profile');

		const response = await fetch(url.toString(), {
			headers: new Headers({
				Authorization: `Bearer ${token}`.trim(),
				...(csrfToken && {
					[CSRF_COOKIE_ID]: csrfToken,
				}),
				'X-BONUSLY-SOURCE': 'internal',
			}),
			credentials: 'include',
		});

		const data = await response.json();

		if (!response.ok) {
			throw new HttpError(response.status, data.redirect_url);
		}

		return data.result as T;
	};

export const fetchProfiles = async (isMobile: boolean) => {
	const { onError } = useIdentityStore.getState();

	try {
		const fetchUserProfile = async () => {
			try {
				const { isLoadingUser, user: existing } = useIdentityStore.getState();

				if (existing) {
					return existing;
				}

				// Instead of making another request to fetch the user, just wait for the user
				// to be fetched from an existing request and return that instead. Same for company
				// profile below
				if (isLoadingUser) {
					return new Promise<User>((resolve) => {
						const unsubscribe = useIdentityStore.subscribe((state) => {
							if (state.isUserFetched || (state.userError && !state.isLoadingUser)) {
								unsubscribe();
								return resolve(state.user);
							}
						});
					});
				}

				useIdentityStore.setState({ isLoadingUser: true });

				const user = await createFetcher<User>('user')();

				useIdentityStore.setState({
					isLoadingUser: false,
					isUserFetched: true,
					user,
				});

				return user;
			} catch (error) {
				onError?.(error);
				useIdentityStore.setState({ isLoadingUser: false, userError: error });
			}
		};

		const fetchCompanyProfile = async () => {
			try {
				const { company: existing, isLoadingCompany } = useIdentityStore.getState();

				if (existing) {
					return existing;
				}

				if (isLoadingCompany) {
					return new Promise<Company>((resolve) => {
						const unsubscribe = useIdentityStore.subscribe((state) => {
							if (state.isCompanyFetched || (state.companyError && !state.isLoadingUser)) {
								unsubscribe();
								return resolve(state.company);
							}
						});
					});
				}

				useIdentityStore.setState({ isLoadingCompany: true });

				const company = await createFetcher<Company>('company')();

				useIdentityStore.setState({
					company,
					isLoadingCompany: false,
					isCompanyFetched: true,
				});

				return company;
			} catch (error) {
				onError?.(error);
				useIdentityStore.setState({
					isLoadingCompany: false,
					companyError: error,
				});
			}
		};

		const [company, user] = await Promise.all([fetchCompanyProfile(), fetchUserProfile()]);

		if (company && user) {
			initializeAnalytics({ company, user });
			initializeIntercom({ company, user, isMobile });
			initializeHotJar({ company, user });
		}

		return { company, user };
	} catch (error) {
		onError?.(error);
		useIdentityStore.setState({ globalError: error });

		return {
			company: useIdentityStore.getState().company,
			user: useIdentityStore.getState().user,
		};
	}
};

export const refetchProfile = async (): Promise<{
	company: Company;
	user: User;
}> => {
	const { onError } = useIdentityStore.getState();

	try {
		const fetchUserProfile = async () => {
			try {
				const user = await createFetcher<User>('user')();

				useIdentityStore.setState({
					user,
				});

				return user;
			} catch (error) {
				onError?.(error);
				useIdentityStore.setState({ userError: error });
			}
		};

		const fetchCompanyProfile = async () => {
			try {
				const company = await createFetcher<Company>('company')();

				useIdentityStore.setState({
					company,
				});

				return company;
			} catch (error) {
				onError?.(error);
				useIdentityStore.setState({
					companyError: error,
				});
			}
		};

		const [company, user] = await Promise.all([fetchCompanyProfile(), fetchUserProfile()]);

		return {
			company: company || useIdentityStore.getState().company,
			user: user || useIdentityStore.getState().user,
		};
	} catch (error) {
		onError?.(error);
		useIdentityStore.setState({ globalError: error });

		return {
			company: useIdentityStore.getState().company,
			user: useIdentityStore.getState().user,
		};
	}
};
